import {cn} from 'platform/shared';
import {match, Pattern} from 'ts-pattern';

import {AriaRole, CSSProperties, ForwardedRef, forwardRef, ReactNode} from 'react';

import {isNotNil} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useFoundationTheme} from '../../hooks/useFoundationTheme';
import {useResponsivePropValue} from '../../hooks/useResponsivePropValue';
import {CSSDimension} from '../../types/CSSDimension';
import {Integer} from '../../types/Integer';
import {ValueByDevice} from '../../types/ValueByDevice';
import {
  DefaultFoundationTheme,
  ThemeColorPath,
  ThemeRadiusPath,
  ThemeShadowsPath,
  ThemeZIndexPath,
} from '../../utils/foundationTheme';
import {getColorByPath} from '../../utils/getColorByPath';
import {getCssSize} from '../../utils/getCssSize';
import {getShadowByPath} from '../../utils/getShadowByPath';
import {getSize} from '../../utils/getSize';
import {getThemeValue} from '../../utils/getThemeValue';

const FOCUS_VISIBLE_CLASSES =
  'focus-visible:[outline:var(--outline-default)_var(--box-focus-outline-color)] focus-visible:[outline-offset:var(--outline-offset)]';

type BoxStyle = CSSProperties & {
  '--box-focus-outline-color'?: string;
};

type BoxTwExtensions = {
  // tw-only extension used by mira; not part of platform/foundation parity API
  center?: boolean;
};

export interface BoxProps extends TestIdProps {
  ref?: ForwardedRef<HTMLDivElement>;
  children?: ReactNode;
  // tw-only extension preserving the old styled-based component's implicit className support
  className?: string;
  role?: AriaRole;
  id?: string;
  backgroundColor?: ThemeColorPath | ValueByDevice<ThemeColorPath | undefined>;
  /**
   * @about Multiplier of the design system's base step
   */
  padding?: Integer | ValueByDevice<Integer>;
  paddingHorizontal?: Integer | ValueByDevice<Integer>;
  paddingVertical?: Integer | ValueByDevice<Integer>;
  paddingTop?: Integer | ValueByDevice<Integer>;
  paddingLeft?: Integer | ValueByDevice<Integer>;
  paddingRight?: Integer | ValueByDevice<Integer>;
  paddingBottom?: Integer | ValueByDevice<Integer>;
  /**
   * Define only width and style
   * Use borderColor to set color
   * @example border="1px solid"
   */
  border?: string | ValueByDevice<string | undefined>;
  borderBottom?: string | ValueByDevice<string | undefined>;
  borderLeft?: string | ValueByDevice<string | undefined>;
  borderRight?: string | ValueByDevice<string | undefined>;
  borderTop?: string | ValueByDevice<string | undefined>;
  borderColor?: ThemeColorPath;
  borderRadius?: ThemeRadiusPath | ValueByDevice<ThemeRadiusPath | undefined>;
  borderTopLeftRadius?: ThemeRadiusPath | ValueByDevice<ThemeRadiusPath | undefined>;
  borderTopRightRadius?: ThemeRadiusPath | ValueByDevice<ThemeRadiusPath | undefined>;
  borderBottomLeftRadius?: ThemeRadiusPath | ValueByDevice<ThemeRadiusPath | undefined>;
  borderBottomRightRadius?: ThemeRadiusPath | ValueByDevice<ThemeRadiusPath | undefined>;
  boxShadow?: ThemeShadowsPath;
  position?: CSSProperties['position'] | ValueByDevice<CSSProperties['position'] | undefined>;
  bottom?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer | undefined>;
  left?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer | undefined>;
  right?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer | undefined>;
  top?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer | undefined>;
  width?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer | undefined>;
  height?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer | undefined>;
  maxHeight?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer | undefined>;
  maxWidth?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer | undefined>;
  minHeight?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer | undefined>;
  minWidth?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer | undefined>;
  zIndex?: ThemeZIndexPath;
  flex?: number | ValueByDevice<number | undefined>;
  flexGrow?: number | ValueByDevice<number | undefined>;
  flexShrink?: number | ValueByDevice<number | undefined>;
  flexBasis?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer | undefined>;
  overflow?: 'auto' | 'hidden' | 'scroll';
  overflowY?: 'auto' | 'hidden' | 'scroll';
  overflowX?: 'auto' | 'hidden' | 'scroll';
  opacity?: number;
  contain?: CSSProperties['contain'];
  transition?: string;
  pointerEvents?: CSSProperties['pointerEvents'];
  ratio?: CSSProperties['aspectRatio'];
  tabIndex?: number;
  onKeyDownCapture?: React.KeyboardEventHandler<HTMLDivElement>;
}

function getRadius(theme: DefaultFoundationTheme, radius: ThemeRadiusPath | undefined) {
  return isNotNil(radius) ? getThemeValue<string>(theme.radii, radius) : undefined;
}

function getZIndex(theme: DefaultFoundationTheme, zIndex: ThemeZIndexPath | undefined) {
  return isNotNil(zIndex)
    ? getThemeValue<CSSProperties['zIndex']>(theme.zIndices, zIndex)
    : undefined;
}

function removeNilStyles(style: BoxStyle): BoxStyle {
  return Object.fromEntries(Object.entries(style).filter(([, value]) => isNotNil(value)));
}

function BoxComponent(props: BoxProps & BoxTwExtensions, ref: ForwardedRef<HTMLDivElement>) {
  const theme = useFoundationTheme();
  const backgroundColor = useResponsivePropValue(props.backgroundColor);
  const padding = useResponsivePropValue(props.padding);
  const paddingVertical = useResponsivePropValue(props.paddingVertical);
  const paddingHorizontal = useResponsivePropValue(props.paddingHorizontal);
  const paddingLeft = useResponsivePropValue(props.paddingLeft);
  const paddingRight = useResponsivePropValue(props.paddingRight);
  const paddingTop = useResponsivePropValue(props.paddingTop);
  const paddingBottom = useResponsivePropValue(props.paddingBottom);
  const border = useResponsivePropValue(props.border);
  const borderBottom = useResponsivePropValue(props.borderBottom);
  const borderLeft = useResponsivePropValue(props.borderLeft);
  const borderRight = useResponsivePropValue(props.borderRight);
  const borderTop = useResponsivePropValue(props.borderTop);
  const borderRadius = useResponsivePropValue(props.borderRadius);
  const borderTopLeftRadius = useResponsivePropValue(props.borderTopLeftRadius);
  const borderTopRightRadius = useResponsivePropValue(props.borderTopRightRadius);
  const borderBottomLeftRadius = useResponsivePropValue(props.borderBottomLeftRadius);
  const borderBottomRightRadius = useResponsivePropValue(props.borderBottomRightRadius);
  const width = useResponsivePropValue(props.width);
  const height = useResponsivePropValue(props.height);
  const maxWidth = useResponsivePropValue(props.maxWidth);
  const maxHeight = useResponsivePropValue(props.maxHeight);
  const minWidth = useResponsivePropValue(props.minWidth);
  const minHeight = useResponsivePropValue(props.minHeight);
  const flex = useResponsivePropValue(props.flex);
  const flexGrow = useResponsivePropValue(props.flexGrow);
  const flexShrink = useResponsivePropValue(props.flexShrink);
  const flexBasis = useResponsivePropValue(props.flexBasis);
  const top = useResponsivePropValue(props.top);
  const left = useResponsivePropValue(props.left);
  const right = useResponsivePropValue(props.right);
  const bottom = useResponsivePropValue(props.bottom);
  const position = useResponsivePropValue(props.position);

  const flexBasisValue = match([isNotNil(flexBasis), isNotNil(flex)])
    .with([true, Pattern.any], () => getCssSize(flexBasis) ?? 'initial')
    .with([false, true], () => '0%')
    .otherwise(() => 'initial');

  const backgroundColorValue = getColorByPath(backgroundColor, theme);
  const borderColorValue = getColorByPath(props.borderColor, theme);
  const style = removeNilStyles({
    '--box-focus-outline-color': backgroundColorValue ?? 'currentColor',
    flexGrow: flexGrow ?? flex ?? 'initial',
    flexShrink: flexShrink ?? flex ?? 'initial',
    flexBasis: flexBasisValue,

    paddingTop: getSize(paddingTop ?? paddingVertical ?? padding ?? 0),
    paddingLeft: getSize(paddingLeft ?? paddingHorizontal ?? padding ?? 0),
    paddingRight: getSize(paddingRight ?? paddingHorizontal ?? padding ?? 0),
    paddingBottom: getSize(paddingBottom ?? paddingVertical ?? padding ?? 0),

    borderBottom,
    borderLeft,
    borderRight,
    borderTop,
    ...(isNotNil(border) ? {border} : {}),
    ...(isNotNil(borderColorValue) ? {borderColor: borderColorValue} : {}),
    borderRadius: getRadius(theme, borderRadius),
    borderTopLeftRadius: getRadius(theme, borderTopLeftRadius),
    borderTopRightRadius: getRadius(theme, borderTopRightRadius),
    borderBottomLeftRadius: getRadius(theme, borderBottomLeftRadius),
    borderBottomRightRadius: getRadius(theme, borderBottomRightRadius),

    top: getCssSize(top),
    left: getCssSize(left),
    right: getCssSize(right),
    bottom: getCssSize(bottom),
    position,

    height: getCssSize(height),
    width: getCssSize(width),
    maxHeight: getCssSize(maxHeight),
    minHeight: getCssSize(minHeight),
    maxWidth: getCssSize(maxWidth),
    minWidth: getCssSize(minWidth),
    aspectRatio: props.ratio ?? 'initial',

    boxShadow: getShadowByPath(props.boxShadow, theme),
    backgroundColor: backgroundColorValue,
    opacity: props.opacity,

    zIndex: getZIndex(theme, props.zIndex),
    contain: props.contain,
    overflow: props.overflow,
    overflowY: props.overflowY,
    overflowX: props.overflowX,
    transition: props.transition,
    pointerEvents: props.pointerEvents,
  });

  return (
    <div
      ref={ref}
      role={props.role}
      id={props.id}
      tabIndex={props.tabIndex}
      className={cn(props.center && 'mx-auto', FOCUS_VISIBLE_CLASSES, props.className)}
      style={style}
      data-testid={suffixTestId('box', props)}
      onKeyDownCapture={props.onKeyDownCapture}
    >
      {props.children}
    </div>
  );
}

export const Box = forwardRef(BoxComponent);
